import { OTHER_SLUG, RESELL_RIGHTS_PURPOSE, isCatalogType, taxonomyTagMatch } from './catalog-taxonomy';
import { rankedDeliverableHints, type DeliverableFile } from './tag-suggestions';

/**
 * A guess at a product's catalogue type and purposes (catalog-taxonomy.ts)
 * from what it already says about itself.
 *
 * TWO USES. The listing forms start the seller's Type and Purpose fields from
 * it, and the marketplace reads it for a product whose seller has not chosen
 * yet (effectiveTaxonomyOf in product-filters.ts), so the listings saved
 * before the fields existed sit on a sensible shelf without a migration.
 *
 * WHAT IT READS. The product's name, short description and AI hint, its tags,
 * its file names and, in the forms, its access links. Not the long
 * description: the marketplace's server first screen does not read it
 * (MARKETPLACE_FIRST_SCREEN_FIELDS), and the browser and the server have to
 * reach the same guess or the purpose bar reorders itself when the live
 * catalogue lands.
 *
 * HOW IT WEIGHS. Each entry has words that name it. A word in the name counts
 * 3, in a tag 2, in the description or the AI hint 1; a word marked weak (a
 * leading "~", such as "planner" for Productivity) counts a third of that.
 * What the files and links point to (rankedDeliverableHints) adds 2 for a
 * certain hint and 1 for a vague one. Ties go to the entry named first in the
 * name, then to list order. A type with one obvious use (a font is design)
 * adds 1 to that purpose, which decides only when nothing else speaks. Scores
 * are kept in thirds of a point, so every sum is a whole number and a tie is a
 * tie in any order of adding.
 *
 * TEACHING NEEDS A WORD ABOUT TEACHING. Teaching (the Education slug) is
 * guessed only beside a word such as teacher, classroom, homeschool, phonics
 * or a school year (TEACHING_WORDS), never from "learn", "handbook" or
 * "student" alone, which shelved adult guides there. The marketplace goes
 * further and shows Teaching only on a product with a year level.
 *
 * RESALE PACKS ARE ONE SHELF. A grant of resale rights (PLR, MRR, "master
 * resell rights") makes Resell rights the only purpose, whatever the pack is
 * about, so a PLR marketing course never sits on the Marketing shelf. Only an
 * explicit grant counts: a bare "resell" or "resale" is far more often the
 * licence line "Personal use only, not for resale.", and a phrase with a "no"
 * or "not" beside it in the same clause ("Resell rights: No") is a refusal.
 * Getting this wrong takes a product off its shelf and shows "Resell rights"
 * on its page, which reads as a grant.
 *
 * FAST, because the marketplace guesses every product it shows that has no
 * stored type or purpose. Phrases are looked up by their first word, so a
 * product costs a few lookups per word, not a pass over every phrase, and a
 * tag's match is remembered (taxonomyTagMatch). A thousand guesses took 600 to
 * 800 ms before both and about 60 ms after (desktop, warm, 2026-10-20); no test
 * pins a time, because the suite runs files in parallel and a timer there is
 * noise, so keep both when changing this.
 *
 * PURE: no React, no Firestore.
 */

export type TaxonomySource = {
  name?: unknown;
  shortDescription?: unknown;
  aiHint?: unknown;
  /** The seller's tags, as stored. */
  tags?: ReadonlyArray<unknown>;
  files?: ReadonlyArray<DeliverableFile>;
  /** Access links, in the forms only (public documents carry none). */
  links?: ReadonlyArray<string>;
  /** A type already chosen, so the purpose guess can lean on it. */
  catalogType?: unknown;
};

export type InferredTaxonomy = {
  /** The best type, or null when nothing points to one. */
  catalogType: string | null;
  /** The best purpose first, then a second when the evidence is real; at most two. */
  purposes: string[];
};

export const MAX_INFERRED_PURPOSES = 2;

/** Scores are counted in thirds of a point, so a weak word is a whole number. */
const THIRDS = 3;

/** A second purpose needs at least one word in the name, or a tag and more. */
const SECOND_PURPOSE_MIN_SCORE = 3 * THIRDS;

/** Points per field; a phrase's strength (3 thirds, or 1 when weak) multiplies them. */
const FIELD_WEIGHT = { name: 3, tags: 2, description: 1 } as const;

/** Words that name each type. A leading "~" marks a weak one. */
const TYPE_WORDS: Readonly<Record<string, readonly string[]>> = {
  template: [
    'template', 'canva', 'notion', 'google sheets', 'google sheet', 'spreadsheet', 'excel', 'google docs',
    'google slides', 'powerpoint', 'keynote', 'pitch deck', 'slide deck', 'framer', 'webflow', 'wordpress theme',
    'shopify theme', '~editable',
  ],
  kit: ['kit', 'toolkit'],
  'planner-journal': ['planner', 'journal', 'agenda', 'tracker', 'logbook', 'diary', 'goodnotes'],
  printable: ['printable', 'worksheet', 'coloring', 'colouring', 'wall art', 'flashcard', 'activity sheet', 'chart', 'certificate'],
  'ebook-guide': ['ebook', 'e book', 'guide', 'handbook', 'workbook', 'playbook', 'blueprint', 'manual', 'glossary', '~framework', '~book'],
  course: ['course', 'masterclass', 'master class', 'bootcamp', 'video course', '~training', '~tutorial', '~workshop', '~class'],
  graphics: [
    'graphic', 'clip art', 'clipart', 'illustration', 'vector', 'icon', 'sticker', 'overlay', 'line art',
    'seamless pattern', 'emote', 'emoji', 'logo', '~element', '~shape', '~background', '~wallpaper', '~png',
  ],
  'ui-kit': ['ui kit', 'uikit', 'design system', 'wireframe', 'component library', 'figma kit', 'dashboard ui'],
  mockup: ['mockup', 'mock up'],
  font: ['font', 'typeface', '~typography'],
  'brushes-textures': ['brush', 'texture', '~stamp'],
  'presets-luts': ['preset', 'lut', 'lightroom', 'color grading', 'colour grading'],
  'stock-media': ['stock photo', 'stock video', 'stock footage', 'footage', 'b roll', '~video', '~reel', '~photo'],
  audio: [
    'sample pack', 'drum kit', 'loop', 'sound effect', 'sfx', 'beat', 'instrumental', 'remix', 'mashup',
    'soundtrack', '~audio', '~song', '~music',
  ],
  'sheet-music': ['sheet music', 'lead sheet', 'chord chart', 'piano score', '~score'],
  pattern: [
    'sewing pattern', 'knitting pattern', 'crochet pattern', 'cross stitch', 'quilt pattern', 'embroidery pattern',
    'amigurumi', 'pattern', '~sewing', '~crochet', '~knitting',
  ],
  'cut-file': [
    'cut file', 'svg cut', 'cricut', 'silhouette cameo', 'laser cut', 'laser engraving', 'laser design', 'dxf',
    'lightburn', 'xtool', 'glowforge',
  ],
  'embroidery-file': ['machine embroidery', 'embroidery design', 'embroidery file', 'pes', 'dst', 'jef', '~embroidery'],
  '3d-model': ['3d model', 'stl', '3d print', '3d printing', 'low poly', '3mf', '3d asset', '~obj'],
  'game-asset': ['game asset', 'sprite', 'tileset', 'tile set', 'pixel art', 'unity asset', 'godot', 'unreal', 'game ready', 'rpg maker'],
  'code-plugin': ['code', 'plugin', 'script', 'boilerplate', 'source code', 'starter code', 'sdk', 'snippet', '~api', '~extension'],
  workflow: ['workflow', 'n8n', 'zapier', 'make com', 'automation template'],
};

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th'];
const ORDINAL_WORDS = [
  'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth',
];

/** "year 3", "grade 3", "3rd grade", "third grade": a school year, in any system's words. */
function schoolYearPhrases(): string[] {
  return ORDINALS.flatMap((ordinal, i) => [`year ${i + 1}`, `grade ${i + 1}`, `${ordinal} grade`, `${ORDINAL_WORDS[i]} grade`]);
}

/**
 * Words about teaching itself: who a teaching resource is for (a teacher, a
 * tutor, a classroom, a homeschool), what only teachers make (a unit plan,
 * task cards, a sub plan) and the years and curricula of school. Teaching
 * (the Education slug) is for resources teachers, tutors and homeschooling
 * parents use with their students (owner, 2026-10-27), so a guess needs one of
 * these (PURPOSE_REQUIRES): "learn", a "handbook", a "beginner" or a "student"
 * in a title had shelved Python handbooks and a student loans course there.
 * A bare "curriculum" is not one (a curriculum vitae is a resume), nor a bare
 * "school" (a back-to-school sale is marketing).
 */
const TEACHING_WORDS: readonly string[] = [
  'teacher', 'teaching', 'classroom', 'tutor', 'tutoring', 'homeschool', 'home school', 'homeschooling',
  'lesson plan', 'unit plan', 'unit of work', 'scheme of work', 'task card', 'sub plan', 'cover lesson',
  'relief lesson', 'bell ringer', 'morning work', 'exit ticket', 'anchor chart', 'word wall', 'brain break',
  'phonics', 'sight word', 'guided reading', 'kindergarten', 'preschool', 'pre k', 'prek', 'montessori',
  'primary school', 'elementary school', 'middle school', 'high school', 'secondary school',
  'esl', 'efl', 'tefl', 'tesol', 'eal', 'australian curriculum', 'national curriculum', 'common core', 'ngss',
  'teks', 'eyfs', 'naplan', 'gcse', 'ks1', 'ks2', 'ks3', 'key stage',
  ...schoolYearPhrases(),
];

/** Words that name each purpose. A leading "~" marks a weak one. */
const PURPOSE_WORDS: Readonly<Record<string, readonly string[]>> = {
  // Counted only beside a word about teaching (PURPOSE_REQUIRES).
  education: [
    ...TEACHING_WORDS,
    'worksheet', 'lesson', 'curriculum', 'student', 'math', 'maths', 'science', 'literacy', 'numeracy', 'spelling',
    'flashcard', 'grammar', 'vocabulary', 'exam', 'quiz', 'school', 'education',
  ],
  business: [
    'business', 'startup', 'entrepreneur', 'pitch deck', 'business plan', 'proposal', 'invoice', 'client',
    'contract', 'sop', 'consultant', 'consulting', 'coach', 'coaching', 'freelancer', 'freelance', 'crm',
    'agency', 'small business', 'onboarding', 'shopify', 'etsy', 'ecommerce', 'e commerce', 'dropshipping',
    'investor', 'print on demand', 'digital product', 'seller', 'passive income', 'side hustle', '~leadership',
    '~management',
  ],
  'marketing-sales': [
    'marketing', 'instagram', 'social media', 'tiktok', 'pinterest', 'facebook', 'ads', 'advertising', 'seo',
    'branding', 'brand', 'email', 'newsletter', 'sales', 'funnel', 'lead magnet', 'content calendar', 'media kit',
    'rate card', 'realtor', 'real estate', 'copywriting', 'influencer', 'campaign', 'viral', 'customer', 'convert',
    'listing presentation', '~post', '~story', '~feed',
  ],
  finance: [
    'budget', 'finance', 'financial', 'expense', 'money', 'savings', 'saving', 'debt', 'bookkeeping', 'accounting',
    'tax', 'investment', 'investing', 'net worth', 'bill', 'paycheck', 'wealth', 'loan', 'mortgage', 'retirement',
    '~invoice',
  ],
  productivity: [
    'productivity', 'habit tracker', 'habit', 'to do list', 'todo list', 'organizer', 'organiser',
    'time management', 'second brain', 'life os', 'notion dashboard', '~planner', '~tracker', '~checklist',
    '~weekly', '~daily', '~schedule', '~routine', '~calendar', '~dashboard', '~notion', '~reset',
  ],
  careers: ['resume', 'cv', 'cover letter', 'interview', 'career', 'job search', 'linkedin', 'job', '~portfolio'],
  'ai-automation': [
    'n8n', 'zapier', 'make com', 'automation', 'automate', 'workflow', 'chatgpt', 'gpt', 'prompt', 'ai agent',
    'midjourney', 'claude', 'llm', 'ai tool', 'artificial intelligence', 'ai prompt',
  ],
  'software-web': [
    'code', 'coding', 'developer', 'programming', 'python', 'javascript', 'typescript', 'sql', 'mysql', 'html',
    'css', 'react', 'nextjs', 'wordpress', 'website', 'web design', 'web development', 'plugin', 'script', 'api',
    'boilerplate', 'saas', 'framer', 'webflow', 'shopify theme', 'data analyst', 'data analytics', '~app',
  ],
  'art-design': [
    'design', 'designer', 'illustration', 'illustrator', 'vector', 'clip art', 'clipart', 'icon', 'graphic', 'logo',
    'mockup', 'font', 'typography', 'brush', 'procreate', 'photoshop', 'texture', 'line art', 'drawing', 'art',
    'artist', 'sticker', 'ui kit', 'figma', 'adobe', '~pattern', '~shape', '~aesthetic',
  ],
  'photo-video': [
    'lightroom', 'lut', 'photography', 'photographer', 'photo', 'video', 'footage', 'premiere', 'after effects',
    'davinci', 'final cut', 'capcut', 'video editing', 'photo editing', 'cinematic', 'filmmaker', 'b roll',
    'camera', '~preset', '~film', '~editing',
  ],
  'music-audio': [
    'music', 'audio', 'sample pack', 'sample', 'loop', 'beat', 'sound effect', 'sfx', 'serum', 'synth',
    'vocal', 'remix', 'mashup', 'song', 'sheet music', 'piano', 'guitar', 'producer', 'drum',
  ],
  games: [
    'game', 'gaming', 'rpg', 'dnd', 'd and d', 'tabletop', 'unity', 'godot', 'unreal', 'sprite', 'tileset',
    'pixel art', 'roblox', 'minecraft', 'board game', 'dungeon', 'esports',
  ],
  'content-creation': [
    'youtube', 'youtuber', 'twitch', 'stream', 'streamer', 'streaming', 'overlay', 'emote', 'thumbnail', 'podcast',
    'vtuber', 'content creator', 'creator', 'faceless', 'reel', 'content engine', 'ugc', '~tiktok', '~content',
  ],
  crafts: [
    'sewing', 'sew', 'knitting', 'knit', 'crochet', 'embroidery', 'cross stitch', 'quilting', 'quilt', 'cricut',
    'silhouette', 'laser', 'engraving', 'stl', '3d print', '3d printing', 'woodworking', 'scrapbooking',
    'papercraft', 'paper craft', 'diy', 'sewing pattern', 'lightburn', 'xtool', 'glowforge', 'machine embroidery',
    'craft', 'crafting', 'handmade', 'amigurumi', '~pattern',
  ],
  'home-family': [
    '~home', 'household', 'family', 'mom', 'mum', 'mother', 'parent', 'parenting', 'kid', 'children', 'child',
    'baby', 'chore', 'cleaning', 'nursery', 'toddler', 'wall art', 'home decor', '~busy',
  ],
  'health-wellbeing': [
    'fitness', 'workout', 'exercise', 'gym', 'yoga', 'pilates', 'meditation', 'mental health', 'therapy',
    'therapist', 'counselling', 'counseling', 'wellness', 'wellbeing', 'well being', 'self care', 'nutrition',
    'health', 'sleep', 'anxiety', 'adhd', 'cbt', 'dbt', '~calm',
  ],
  'food-drink': [
    'recipe', 'cookbook', 'cooking', 'baking', 'meal', 'meal plan', 'meal prep', 'food', 'drink', 'cocktail',
    'coffee', 'kitchen', 'grocery', 'menu',
  ],
  travel: ['travel', 'trip', 'itinerary', 'vacation', 'packing list', 'road trip', 'backpacking', 'tourism', 'airbnb'],
  'events-celebrations': [
    'wedding', 'bridal', 'bride', 'party', 'birthday', 'invitation', 'invite', 'christmas', 'holiday', 'halloween',
    'thanksgiving', 'easter', 'baby shower', 'bridal shower', 'celebration', 'event', 'anniversary', 'graduation',
    'new year', 'valentine', 'party favor', 'party favour', 'december',
  ],
  'personal-growth': [
    'affirmation', 'manifestation', 'manifest', 'gratitude', 'self help', 'self improvement', 'mindset',
    'motivation', 'goal', 'vision board', 'spiritual', 'spirituality', 'faith', 'bible', 'christian', 'prayer',
    'church', 'devotional', 'mindfulness', 'confidence', '~journal',
  ],
  'writing-publishing': [
    'kdp', 'author', 'writing', 'writer', 'novel', 'publishing', 'publish', 'manuscript', 'low content',
    'book cover', 'poetry', 'ghostwriting', '~book', '~story',
  ],
};

/**
 * Grants of resale rights. Any one, unrefused, makes Resell rights the only
 * purpose. Not bare "resell" or "resale" (see the header), nor bare "private
 * label" or "white label", which name businesses as often as rights.
 */
const RESALE_WORDS: readonly string[] = [
  'plr', 'mrr', 'resell right', 'resale right', 'reseller right', 'reselling right', 'master resell', 'master resale',
  'private label right', 'white label right', 'resell license', 'resell licence', 'resale license', 'resale licence',
  'reseller license', 'reseller licence', 'sell as your own',
];

/** A word that, in the same clause as a grant, refuses it: "no PLR", "resell rights not included". */
const NEGATIONS: ReadonlySet<string> = new Set([
  'no', 'not', 'non', 'nor', 'without', 'never', 'cannot', 'cant', 'dont', 'doesnt', 'isnt', 'arent', 'wont',
  'excluded', 'prohibited', 'forbidden',
]);
/** How far a refusal reaches: three words before the grant, two after. */
const NEGATION_BEFORE = 3;
const NEGATION_AFTER = 2;

/** The purpose a type all but always serves. Adds 1, so it decides only alone. */
const TYPE_PURPOSE_PRIOR: Readonly<Record<string, string>> = {
  font: 'art-design',
  'brushes-textures': 'art-design',
  graphics: 'art-design',
  mockup: 'art-design',
  'ui-kit': 'art-design',
  'presets-luts': 'photo-video',
  'stock-media': 'photo-video',
  audio: 'music-audio',
  'sheet-music': 'music-audio',
  pattern: 'crafts',
  'cut-file': 'crafts',
  'embroidery-file': 'crafts',
  '3d-model': 'crafts',
  'game-asset': 'games',
  'code-plugin': 'software-web',
  workflow: 'ai-automation',
  'planner-journal': 'productivity',
  // Not course: Teaching, which a course once suggested, is classroom
  // resources, and most courses teach adults a skill.
};

/**
 * Purposes guessed only when one of these words is present, however the rest
 * of their words score. Teaching's other words (a worksheet, a quiz, maths)
 * add to its score once one of its teaching words is there.
 */
const PURPOSE_REQUIRES: Readonly<Record<string, readonly string[]>> = {
  education: TEACHING_WORDS,
};

/** What a deliverable hint (a HOME_TAGS slug) says about the type. */
const HINT_TYPE: Readonly<Record<string, string>> = {
  templates: 'template',
  notion: 'template',
  canva: 'template',
  planners: 'planner-journal',
  printables: 'printable',
  ebooks: 'ebook-guide',
  courses: 'course',
  fonts: 'font',
  icons: 'graphics',
  graphics: 'graphics',
  mockups: 'mockup',
  brushes: 'brushes-textures',
  presets: 'presets-luts',
  'ui-kits': 'ui-kit',
  '3d-models': '3d-model',
  code: 'code-plugin',
  plugins: 'code-plugin',
  music: 'audio',
  'sound-effects': 'audio',
  'game-assets': 'game-asset',
};

/** File types that name a type the deliverable hints have no slug for. Certain. */
const EXTRA_EXTENSION_TYPE: Readonly<Record<string, string>> = {
  pes: 'embroidery-file',
  dst: 'embroidery-file',
  jef: 'embroidery-file',
  exp: 'embroidery-file',
  vp3: 'embroidery-file',
  hus: 'embroidery-file',
  pec: 'embroidery-file',
  vip: 'embroidery-file',
  dxf: 'cut-file',
  musicxml: 'sheet-music',
  mxl: 'sheet-music',
};

/** One entry's word or words, and how much it counts: 3 thirds, or 1 when weak. */
type Phrase = { slug: string; words: string[]; strength: number };

/** Phrases by the word they start with, so a product's words look up only their own. */
type PhraseIndex = ReadonlyMap<string, readonly Phrase[]>;

/** The forms a phrase's last word matches: "templates", "boxes", "diaries". */
function lastWordForms(word: string): string[] {
  const forms = [word, `${word}s`, `${word}es`];
  if (word.endsWith('y')) forms.push(`${word.slice(0, -1)}ies`);
  return forms;
}

function lastWordMatches(token: string, word: string): boolean {
  return lastWordForms(word).includes(token);
}

function indexOf(table: Readonly<Record<string, readonly string[]>>): PhraseIndex {
  const index = new Map<string, Phrase[]>();
  for (const [slug, entries] of Object.entries(table)) {
    for (const entry of entries) {
      const weak = entry.startsWith('~');
      const phrase: Phrase = { slug, words: (weak ? entry.slice(1) : entry).split(' '), strength: weak ? 1 : THIRDS };
      // A one-word phrase is its own last word, so any of its forms starts it.
      const keys = phrase.words.length === 1 ? lastWordForms(phrase.words[0]) : [phrase.words[0]];
      for (const key of keys) {
        const list = index.get(key);
        if (!list) index.set(key, [phrase]);
        else if (!list.includes(phrase)) list.push(phrase);
      }
    }
  }
  return index;
}

const TYPE_INDEX = indexOf(TYPE_WORDS);
const PURPOSE_INDEX = indexOf(PURPOSE_WORDS);
const REQUIRED_INDEX: ReadonlyMap<string, PhraseIndex> = new Map(
  Object.entries(PURPOSE_REQUIRES).map(([slug, words]) => [slug, indexOf({ [slug]: words })]),
);
const RESALE_INDEX = indexOf({ [RESELL_RIGHTS_PURPOSE]: RESALE_WORDS });
const TYPE_ORDER = Object.keys(TYPE_WORDS);
const PURPOSE_ORDER = Object.keys(PURPOSE_WORDS);

/** Lowercase words: "&" reads as "and", apostrophes go, anything else splits. */
function wordsOf(text: unknown): string[] {
  if (typeof text !== 'string') return [];
  return text
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['‘’ʼ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * The words of each clause: text split at sentence ends, commas, semicolons,
 * bullets, bars and slashes, so a refusal in one clause ("Giveaway rights: no,
 * resell rights: yes") never reaches a grant in the next. A colon does not
 * split: "Resell rights: No" is one clause.
 */
function clausesOf(text: unknown): string[][] {
  if (typeof text !== 'string') return [];
  return text.split(/[.!?;,|/\n\r•·]+/).map(wordsOf).filter((words) => words.length > 0);
}

/** Whether `words` starts at `tokens[at]`, its last word in any of its forms. */
function phraseStartsAt(tokens: readonly string[], at: number, words: readonly string[]): boolean {
  const last = words.length - 1;
  if (at + last >= tokens.length) return false;
  for (let offset = 0; offset <= last; offset += 1) {
    const token = tokens[at + offset];
    if (offset === last ? !lastWordMatches(token, words[offset]) : token !== words[offset]) return false;
  }
  return true;
}

/** Each phrase found in `tokens`, with where it first starts. */
function phrasesIn(tokens: readonly string[], index: PhraseIndex): Map<Phrase, number> {
  const found = new Map<Phrase, number>();
  for (let at = 0; at < tokens.length; at += 1) {
    const candidates = index.get(tokens[at]);
    if (!candidates) continue;
    for (const phrase of candidates) {
      if (!found.has(phrase) && phraseStartsAt(tokens, at, phrase.words)) found.set(phrase, at);
    }
  }
  return found;
}

/** Whether a clause grants resale rights: a grant with no refusal beside it. */
function grantsResale(clause: readonly string[]): boolean {
  for (let at = 0; at < clause.length; at += 1) {
    for (const phrase of RESALE_INDEX.get(clause[at]) ?? []) {
      if (!phraseStartsAt(clause, at, phrase.words)) continue;
      const end = at + phrase.words.length;
      const near = [...clause.slice(Math.max(0, at - NEGATION_BEFORE), at), ...clause.slice(end, end + NEGATION_AFTER)];
      if (!near.some((word) => NEGATIONS.has(word))) return true;
    }
  }
  return false;
}

type Tally = Map<string, { score: number; firstInName: number }>;

function add(tally: Tally, slug: string, score: number, nameAt = Infinity) {
  const entry = tally.get(slug);
  if (entry) {
    entry.score += score;
    entry.firstInName = Math.min(entry.firstInName, nameAt);
  } else {
    tally.set(slug, { score, firstInName: nameAt });
  }
}

/**
 * Score the phrases found in the name, the tags and the description. A phrase
 * counts once per field it appears in; a tag is matched on its own words only,
 * so two neighbouring tags cannot make a phrase between them.
 *
 * @returns What the tags field gave each entry, so a tag that names one adds
 *   only what that leaves short of a full tag's weight (creditNamed).
 */
function scorePhrases(index: PhraseIndex, name: string[], tags: string[][], description: string[], tally: Tally): Map<string, number> {
  for (const [phrase, at] of phrasesIn(name, index)) add(tally, phrase.slug, FIELD_WEIGHT.name * phrase.strength, at);
  const inTags = new Set<Phrase>();
  for (const tag of tags) {
    for (const phrase of phrasesIn(tag, index).keys()) inTags.add(phrase);
  }
  const fromTags = new Map<string, number>();
  for (const phrase of inTags) {
    const score = FIELD_WEIGHT.tags * phrase.strength;
    add(tally, phrase.slug, score);
    fromTags.set(phrase.slug, (fromTags.get(phrase.slug) ?? 0) + score);
  }
  for (const phrase of phrasesIn(description, index).keys()) {
    add(tally, phrase.slug, FIELD_WEIGHT.description * phrase.strength);
  }
  return fromTags;
}

/**
 * A tag that names an entry gives it a full tag's weight, counting what the
 * other tags' phrases already gave it: the tags are one field, and a field
 * speaks for an entry once. Without that, "templates" beside "canva-template"
 * counted twice and outweighed a name that said "Planner".
 */
function creditNamed(tally: Tally, named: ReadonlySet<string>, fromTags: ReadonlyMap<string, number>) {
  const full = FIELD_WEIGHT.tags * THIRDS;
  for (const slug of named) {
    const given = fromTags.get(slug) ?? 0;
    if (given < full) add(tally, slug, full - given);
  }
}

/** Best first: score, then named earliest in the name, then list order. */
function ranked(tally: Tally, order: readonly string[]): Array<{ slug: string; score: number }> {
  return Array.from(tally, ([slug, { score, firstInName }]) => ({ slug, score, firstInName }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.firstInName - b.firstInName || order.indexOf(a.slug) - order.indexOf(b.slug))
    .map(({ slug, score }) => ({ slug, score }));
}

function extensionOf(name: unknown): string {
  if (typeof name !== 'string') return '';
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Whether the product grants resale rights: a grant in a clause of its name,
 * description or AI hint, or a tag that IS Resell rights ("plr", "mrr",
 * "resell rights"; also a bare "resale" tag, as the tag picker reads it). Not
 * a longer tag that merely contains one: "plr-guide" sat among a dozen search
 * tags on a chores chart for parents (seen on the live catalogue, 2026-10-20),
 * and a pack that really carries the rights says so in its name or
 * description.
 */
function sellsResaleRights(source: TaxonomySource, tags: readonly string[]): boolean {
  return [source.name, source.shortDescription, source.aiHint].some((text) => clausesOf(text).some(grantsResale))
    || tags.some((tag) => taxonomyTagMatch(tag)?.slug === RESELL_RIGHTS_PURPOSE);
}

/** Whether any of the fields holds one of the words a purpose needs (PURPOSE_REQUIRES). */
function hasRequiredWords(slug: string, name: string[], tags: string[][], description: string[]): boolean {
  const index = REQUIRED_INDEX.get(slug);
  if (!index) return true;
  return [name, ...tags, description].some((tokens) => phrasesIn(tokens, index).size > 0);
}

export type InferOptions = {
  /**
   * Purposes the guess must not give, so the best of the rest wins. The
   * marketplace leaves Teaching out for a product with no year level
   * (effectiveTaxonomyOf in product-filters.ts).
   */
  excludePurposes?: readonly string[];
};

export function inferCatalogTaxonomy(source: TaxonomySource, options: InferOptions = {}): InferredTaxonomy {
  const name = wordsOf(source.name);
  const rawTags = (Array.isArray(source.tags) ? source.tags : []).filter((tag): tag is string => typeof tag === 'string');
  // A tag that names an entry ("templates", "software", an old tag from before
  // these were fields) is that entry, counted at a tag's weight whether or not
  // the word lists above carry it. Every other tag is read for phrases.
  const namedTypes = new Set<string>();
  const namedPurposes = new Set<string>();
  const tags: string[][] = [];
  for (const tag of rawTags) {
    const match = taxonomyTagMatch(tag);
    if (!match) {
      const words = wordsOf(tag);
      if (words.length > 0) tags.push(words);
    } else if (match.slug !== OTHER_SLUG) {
      (match.kind === 'type' ? namedTypes : namedPurposes).add(match.slug);
    }
  }
  const description = [...wordsOf(source.shortDescription), ...wordsOf(source.aiHint)];
  const files = (Array.isArray(source.files) ? source.files : []).filter(
    (file): file is DeliverableFile => !!file && typeof (file as DeliverableFile).name === 'string',
  );
  const links = (Array.isArray(source.links) ? source.links : []).filter((link): link is string => typeof link === 'string');

  // TYPE
  const types: Tally = new Map();
  creditNamed(types, namedTypes, scorePhrases(TYPE_INDEX, name, tags, description, types));
  for (const hint of rankedDeliverableHints({ files, links })) {
    const type = HINT_TYPE[hint.slug];
    if (type) add(types, type, (hint.strong ? 2 : 1) * THIRDS);
  }
  for (const file of files) {
    const type = EXTRA_EXTENSION_TYPE[extensionOf(file.name)];
    if (type) add(types, type, 2 * THIRDS);
  }
  const chosenType = isCatalogType(source.catalogType) && source.catalogType !== OTHER_SLUG ? source.catalogType : null;
  const catalogType = ranked(types, TYPE_ORDER)[0]?.slug ?? null;

  // PURPOSES
  if (sellsResaleRights(source, rawTags)) return { catalogType, purposes: [RESELL_RIGHTS_PURPOSE] };

  const purposes: Tally = new Map();
  creditNamed(purposes, namedPurposes, scorePhrases(PURPOSE_INDEX, name, tags, description, purposes));
  const prior = TYPE_PURPOSE_PRIOR[chosenType ?? catalogType ?? ''];
  if (prior) add(purposes, prior, 1 * THIRDS);
  for (const slug of purposes.keys()) {
    if (!hasRequiredWords(slug, name, tags, description) || options.excludePurposes?.includes(slug)) purposes.delete(slug);
  }
  const best = ranked(purposes, PURPOSE_ORDER);
  const picked = best.slice(0, 1).map((entry) => entry.slug);
  const second = best[1];
  if (second && second.score >= SECOND_PURPOSE_MIN_SCORE) picked.push(second.slug);
  return { catalogType, purposes: picked.slice(0, MAX_INFERRED_PURPOSES) };
}
